import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import AuthService from '../services/authService';
import PostService from '../services/postService';
import ClubService from '../services/clubService';
import UserService from '../services/userService';
import CollegeService from '../services/collegeService';
import {
  AuthUser,
  Post,
  Club,
  PendingStudent,
  LoadingState,
  ErrorState,
  AppContextType,
  SignUpForm
} from '../types';
import { handleApiError } from '../services/api';

/**
 * Default context value
 */
const defaultContextValue: AppContextType = {
  currentUser: null,
  setCurrentUser: () => {},
  authInitialized: false,
  clubs: [],
  posts: [],
  pendingStudents: [],
  loading: {},
  error: {},
  refreshPosts: async () => {},
  refreshClubs: async () => {},
  refreshPendingStudents: async () => {},
  refreshUser: async () => {},
  addPost: async () => {},
  likePost: async () => {},
  approveStudent: async () => {},
  updateUserGoals: async () => {},
  login: async () => {},
  register: async () => {},
  logout: () => {},
};

/**
 * App Context
 */
const AppContext = createContext<AppContextType>(defaultContextValue);

/**
 * App Provider Component
 */
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // State
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [pendingStudents, setPendingStudents] = useState<PendingStudent[]>([]);
  const [loading, setLoading] = useState<LoadingState>({
    auth: false,
    posts: false,
    clubs: false,
    pendingStudents: false,
    user: false,
  });
  const [error, setError] = useState<ErrorState>({
    auth: null,
    posts: null,
    clubs: null,
    pendingStudents: null,
    user: null,
  });

  // Helper function to update loading state
  const updateLoading = useCallback((key: string, value: boolean) => {
    setLoading(prev => ({ ...prev, [key]: value }));
  }, []);

  // Helper function to update error state
  const updateError = useCallback((key: string, value: string | null) => {
    setError(prev => ({ ...prev, [key]: value }));
  }, []);

  // ============ AUTH ACTIONS ============

  const login = useCallback(async (email: string, password: string) => {
    updateLoading('auth', true);
    updateError('auth', null);

    try {
      const result = await AuthService.login({ email, password });

      // Check if email verification is required
      if (result.emailVerified === false && result.data?.requiresVerification) {
        updateLoading('auth', false);
        // Throw special error for email verification
        const verificationError = new Error('Email verification required');
        (verificationError as any).emailVerified = false;
        (verificationError as any).email = result.data?.user?.email || email;
        (verificationError as any).name = result.data?.user?.name;
        throw verificationError;
      }

      const { user } = result;

      // Check if user exists (successful login)
      if (!user) {
        throw new Error('Invalid login response');
      }
      setCurrentUser(user);

      // Store college data if user has collegeId and collegeName
      if (user.collegeId && user.collegeName) {
        try {
          CollegeService.setCollegeData({
            id: user.collegeId,
            name: user.collegeName,
            logo: undefined,
            storedAt: new Date().toISOString()
          } as any);
        } catch (error) {
          // Silent fail for college data storage
        }
      }

      // Don't navigate here - let the component handle navigation
      // This prevents page refresh before errors can be displayed
      updateLoading('auth', false);
    } catch (error: any) {
      updateLoading('auth', false);

      // Don't set error message for email verification - let LoginPage handle it
      if (!error.emailVerified && error.emailVerified !== false) {
        const errorMessage = handleApiError(error);
        updateError('auth', errorMessage);
      }

      throw error;
    }
  }, [updateLoading, updateError]);

  const register = useCallback(async (userData: SignUpForm) => {
    updateLoading('auth', true);
    updateError('auth', null);

    try {
      const { user } = await AuthService.register(userData);
      setCurrentUser(user);

      // Store college data if user has collegeId
      if (user.collegeId) {
        try {
          await CollegeService.getCollegeInfo(user.collegeId);
        } catch (error) {
          console.warn('Failed to fetch college data on registration:', error);
        }
      }

      // Navigate to the appropriate page after successful registration
      // Most new registrations will not be verified immediately
      if (user.isVerified) {
        window.location.href = '/clubs';
      } else {
        window.location.href = '/pending-approval';
      }
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      updateError('auth', errorMessage);
      throw error;
    } finally {
      updateLoading('auth', false);
    }
  }, [updateLoading, updateError]);

  const logout = useCallback(() => {
    AuthService.logout();
    setCurrentUser(null);
    setClubs([]);
    setPosts([]);
    setPendingStudents([]);
    setError({});

    // Clear college data from localStorage
    CollegeService.clearCollegeData();
  }, []);

  // ============ DATA REFRESH ACTIONS ============

  const refreshUser = useCallback(async () => {
    if (!currentUser) return;

    updateLoading('user', true);
    updateError('user', null);

    try {
      const updatedUser = await UserService.getCurrentUser();
      setCurrentUser(updatedUser as AuthUser);
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      updateError('user', errorMessage);
      console.error('Failed to refresh user:', error);
    } finally {
      updateLoading('user', false);
    }
  }, [currentUser, updateLoading, updateError]);

  const refreshPosts = useCallback(async () => {
    updateLoading('posts', true);
    updateError('posts', null);

    try {
      const { items } = await PostService.getPosts(1, 20);
      // Ensure items is always an array
      setPosts(Array.isArray(items) ? items : []);
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      updateError('posts', errorMessage);
      console.error('Failed to refresh posts:', error);
      // Set empty array on error
      setPosts([]);
    } finally {
      updateLoading('posts', false);
    }
  }, [updateLoading, updateError]);

  const refreshClubs = useCallback(async () => {
    updateLoading('clubs', true);
    updateError('clubs', null);

    try {
      const { items } = await ClubService.getClubs();
      // Ensure items is always an array
      setClubs(Array.isArray(items) ? items : []);
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      updateError('clubs', errorMessage);
      console.error('Failed to refresh clubs:', error);
      // Set empty array on error
      setClubs([]);
    } finally {
      updateLoading('clubs', false);
    }
  }, [updateLoading, updateError]);

  const refreshPendingStudents = useCallback(async () => {
    if (currentUser?.role !== 'Faculty') return;

    updateLoading('pendingStudents', true);
    updateError('pendingStudents', null);

    try {
      const students = await UserService.getPendingStudents();
      // Ensure students is always an array
      setPendingStudents(Array.isArray(students) ? students : []);
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      updateError('pendingStudents', errorMessage);
      console.error('Failed to refresh pending students:', error);
      // Set empty array on error
      setPendingStudents([]);
    } finally {
      updateLoading('pendingStudents', false);
    }
  }, [currentUser?.role, updateLoading, updateError]);

  // ============ POST ACTIONS ============

  const addPost = useCallback(async (content: string, image?: string) => {
    try {
      const newPost = await PostService.createPost({ content, image });
      setPosts(prev => [newPost, ...prev]);
    } catch (error: any) {
      console.error('Failed to create post:', error);
      throw error;
    }
  }, []);

  const likePost = useCallback(async (postId: string) => {
    try {
      const { isLiked, likesCount } = await PostService.toggleLike(postId);
      setPosts(prev => Array.isArray(prev) ? prev.map(post =>
        post.id === postId
          ? { ...post, likes: likesCount, isLiked }
          : post
      ) : []);
    } catch (error: any) {
      console.error('Failed to like post:', error);
      throw error;
    }
  }, []);

  // ============ USER ACTIONS ============

  const approveStudent = useCallback(async (studentId: string) => {
    try {
      await UserService.approveStudent(studentId);
      setPendingStudents(prev => Array.isArray(prev) ? prev.filter(s => s && s.id !== studentId) : []);
    } catch (error: any) {
      console.error('Failed to approve student:', error);
      throw error;
    }
  }, []);

  const updateUserGoals = useCallback(async (goals: string) => {
    try {
      const updatedUser = await UserService.updateGoals(goals);
      // Ensure type compatibility by providing required fields
      const authUser: AuthUser = {
        ...updatedUser,
        branch: updatedUser.branch || currentUser?.branch || '',
        collegeId: updatedUser.collegeId || currentUser?.collegeId || '',
        collegeName: updatedUser.collegeName || currentUser?.collegeName,
        emailVerified: updatedUser.emailVerified ?? currentUser?.emailVerified ?? false,
        isVerified: updatedUser.isVerified ?? currentUser?.isVerified ?? false,
      };
      setCurrentUser(authUser);
    } catch (error: any) {
      console.error('Failed to update goals:', error);
      throw error;
    }
  }, [currentUser]);

  // ============ INITIALIZATION ============

  useEffect(() => {
    const initializeAuth = async () => {
      const { user, isAuthenticated } = AuthService.initializeAuth();

      if (isAuthenticated && user) {
        setCurrentUser(user);

        // Validate session in background and ensure college data is available
        try {
          const validUser = await AuthService.getCurrentUser();
          setCurrentUser(validUser);

          // Ensure college data is stored if user has collegeId
          if (validUser.collegeId) {
            try {
              await CollegeService.getCollegeInfo(validUser.collegeId);
            } catch (error) {
              console.warn('Failed to fetch college data during initialization:', error);
            }
          }
        } catch (error) {
          // Session invalid, logout
          logout();
        }
      }

      // Mark auth as initialized regardless of whether user is authenticated
      setAuthInitialized(true);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Load initial data when user is authenticated
  useEffect(() => {
    if (currentUser && currentUser.isVerified) {
      // Load data in parallel
      Promise.all([
        refreshPosts(),
        refreshClubs(),
        (currentUser.role === 'Faculty' || currentUser.role === 'Ambassador') ? refreshPendingStudents() : Promise.resolve(),
      ]).catch(error => {
        console.error('Failed to load initial data:', error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, currentUser?.isVerified, currentUser?.role]);

  // Context value
  const contextValue: AppContextType = {
    currentUser,
    setCurrentUser,
    authInitialized,
    clubs,
    posts,
    pendingStudents,
    loading,
    error,
    refreshPosts,
    refreshClubs,
    refreshPendingStudents,
    refreshUser,
    addPost,
    likePost,
    approveStudent,
    updateUserGoals,
    login,
    register,
    logout,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

/**
 * Custom hook to use App Context
 */
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

/**
 * Custom hook for authentication state
 */
export const useAuth = () => {
  const { currentUser, authInitialized, login, register, logout, loading, error } = useApp();

  return {
    user: currentUser,
    isAuthenticated: !!currentUser,
    isVerified: currentUser?.isVerified || false,
    isFaculty: currentUser?.role === 'Faculty' || currentUser?.role === 'Ambassador',
    isStudent: currentUser?.role === 'Student',
    isAmbassador: currentUser?.role === 'Ambassador',
    authInitialized,
    login,
    register,
    logout,
    loading: loading.auth || false,
    error: error.auth || null,
  };
};

/**
 * Custom hook for posts
 */
export const usePosts = () => {
  const { posts, addPost, likePost, refreshPosts, loading, error } = useApp();

  return {
    posts,
    addPost,
    likePost,
    refreshPosts,
    loading: loading.posts || false,
    error: error.posts || null,
  };
};

/**
 * Custom hook for clubs
 */
export const useClubs = () => {
  const { clubs, refreshClubs, loading, error } = useApp();

  return {
    clubs,
    refreshClubs,
    loading: loading.clubs || false,
    error: error.clubs || null,
  };
};

/**
 * Custom hook for pending students (Faculty only)
 */
export const usePendingStudents = () => {
  const { currentUser, pendingStudents, approveStudent, refreshPendingStudents, loading, error } = useApp();

  return {
    pendingStudents,
    approveStudent,
    refreshPendingStudents,
    loading: loading.pendingStudents || false,
    error: error.pendingStudents || null,
    canApprove: currentUser?.role === 'Faculty' || currentUser?.role === 'Ambassador',
  };
};

export default AppContext;